import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  addPackageSupplyRequest,
  createPackageRequest,
  deletePackageRequest,
  dispatchOrderRequest,
  getPackagesRequest,
  getPackingOptionsRequest,
  getPicklistRequest,
  labelDeliveryRequest,
  pickLineRequest,
  removePackageSupplyRequest,
  reserveOrderRequest,
  shortPickLineRequest,
  unpickLineRequest,
  updatePackageRequest,
} from './fulfillmentApi';
import { getWmsOrderRequest } from '../wmsOrders/wmsOrdersApi';
import { buildPickPayload, buildShortPickPayload, buildUnpickPayload } from './fulfillmentUtils';
import { extractApiError } from '../../utils/errors';

/**
 * Everything the screen needs to prepare one order: the order with its
 * warehouse state, the lines to pick with where to take them from, and the
 * parcels of its shipment.
 *
 * The order is the one that can fail the whole load: without it there is no
 * screen. The other three — the picklist, the parcels and the packing supplies
 * — are asked together after it, and each may fail on its own: the picklist
 * answers 403 when the module is off for that client, and the order already
 * says so. Then the screen opens with that note and an empty list rather than
 * a red page.
 *
 * @param {number|string} orderId
 * @returns {Promise<{order: object, lines: Array<object>, packages: Array<object>,
 *   supplyOptions: Array<object>}>}
 */
export const fetchFulfillment = createAsyncThunk(
  'fulfillment/fetchFulfillment',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data: order } = await getWmsOrderRequest(orderId);

      const [picklist, packages, options] = await Promise.allSettled([
        getPicklistRequest(orderId),
        getPackagesRequest(orderId),
        getPackingOptionsRequest(orderId),
      ]);
      [picklist, packages, options].forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Error fetching fulfillment part ${index}:`, result.reason);
        }
      });

      return {
        order,
        lines: picklist.status === 'fulfilled' ? picklist.value.data.lines || [] : [],
        packages:
          packages.status === 'fulfilled'
            ? packages.value.data.results || packages.value.data.items || []
            : [],
        supplyOptions: options.status === 'fulfilled' ? options.value.data.supplies || [] : [],
      };
    } catch (error) {
      console.error('Error fetching fulfillment:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo abrir la orden'));
    }
  }
);

/** Runs a write and reloads the whole screen after it: every act moves the state. */
const writeThenReload = (type, fallback, request) =>
  createAsyncThunk(type, async (arg, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await request(arg);
      dispatch(fetchFulfillment(arg.orderId));
      return data;
    } catch (error) {
      console.error(`Error on ${type}:`, error);
      return rejectWithValue(extractApiError(error, fallback));
    }
  });

/**
 * Commits available stock to what every line still lacks. Resolves with
 * `reserved_now`, `lines_complete` and `lines_total`.
 *
 * @param {{orderId: number}} params
 */
export const reserveOrder = writeThenReload(
  'fulfillment/reserveOrder',
  'No se pudo reservar',
  ({ orderId }) => reserveOrderRequest(orderId)
);

/**
 * Takes units out of a location for a line.
 *
 * @param {{orderId: number, line: object, suggestion: object, quantity: number|string,
 *   lot?: string, scannedCode?: string}} params
 * @returns {Promise<{capacity_warning: string|null}>}
 */
export const pickLine = writeThenReload(
  'fulfillment/pickLine',
  'No se pudo pickear',
  ({ line, suggestion, quantity, lot, scannedCode }) =>
    pickLineRequest(
      line.order_product_id,
      buildPickPayload({ suggestion, quantity, lot, scannedCode })
    )
);

/**
 * Reports that the location did not hold what the system said.
 *
 * @param {{orderId: number, line: object, suggestion: object, found: number|string,
 *   reason: string, lot?: string, notes?: string}} params
 */
export const shortPickLine = writeThenReload(
  'fulfillment/shortPickLine',
  'No se pudo registrar el desvío',
  ({ line, suggestion, found, reason, lot, notes }) =>
    shortPickLineRequest(
      line.order_product_id,
      buildShortPickPayload({ suggestion, found, reason, lot, notes })
    )
);

/**
 * Puts picked units back on a location.
 *
 * @param {{orderId: number, line: object, slotId: number, quantity: number|string,
 *   lot?: string, reason?: string}} params
 */
export const unpickLine = writeThenReload(
  'fulfillment/unpickLine',
  'No se pudo deshacer el picking',
  ({ line, slotId, quantity, lot, reason }) =>
    unpickLineRequest(line.order_product_id, buildUnpickPayload({ slotId, quantity, lot, reason }))
);

/** @param {{orderId: number}} params */
export const addPackage = writeThenReload(
  'fulfillment/addPackage',
  'No se pudo agregar el bulto',
  ({ orderId }) => createPackageRequest(orderId)
);

/** @param {{orderId: number, packageId: number}} params */
export const removePackage = writeThenReload(
  'fulfillment/removePackage',
  'No se pudo quitar el bulto',
  ({ packageId }) => deletePackageRequest(packageId)
);

/**
 * Saves the logistic variables of one parcel.
 *
 * This is the one write that does not reload everything: the fields save on
 * blur, four per parcel, and the answer already carries the parcel as it is
 * now. Only when closing it changed the shipment's state is the order read
 * again, because that is the moment the warehouse state moves.
 *
 * @param {{orderId: number, packageId: number, values: object}} params
 * @returns {Promise<object>} The parcel as the server left it, with
 *   `delivery_status_changed`.
 */
export const savePackage = createAsyncThunk(
  'fulfillment/savePackage',
  async ({ orderId, packageId, values }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await updatePackageRequest(packageId, values);
      if (data.delivery_status_changed) dispatch(fetchFulfillment(orderId));
      return data;
    } catch (error) {
      console.error('Error saving package:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo guardar el bulto'));
    }
  }
);

/**
 * What is left of each packing supply, after one was used or put back. Only
 * the options are read again: the parcel itself came in the answer.
 *
 * @param {number|string} orderId
 */
export const fetchPackingOptions = createAsyncThunk(
  'fulfillment/fetchPackingOptions',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await getPackingOptionsRequest(orderId);
      return data.supplies || [];
    } catch (error) {
      console.error('Error fetching packing options:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los insumos'));
    }
  }
);

/** Runs a write on a parcel's supplies: the answer is the parcel, and the options are refreshed. */
const packageSupplyWrite = (type, fallback, request) =>
  createAsyncThunk(type, async (arg, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await request(arg);
      dispatch(fetchPackingOptions(arg.orderId));
      return data;
    } catch (error) {
      console.error(`Error on ${type}:`, error);
      return rejectWithValue(extractApiError(error, fallback));
    }
  });

/**
 * Uses a supply in a parcel. Resolves with the parcel as the server left it.
 *
 * @param {{orderId: number, packageId: number, supplyId: number, quantity: number|string}} params
 */
export const addPackageSupply = packageSupplyWrite(
  'fulfillment/addPackageSupply',
  'No se pudo usar el insumo',
  ({ packageId, supplyId, quantity }) =>
    addPackageSupplyRequest(packageId, { supply_id: supplyId, quantity: Number(quantity) })
);

/**
 * Takes a supply out of a parcel. Resolves with the parcel as the server left it.
 *
 * @param {{orderId: number, packageId: number, packageSupplyId: number}} params
 */
export const removePackageSupply = packageSupplyWrite(
  'fulfillment/removePackageSupply',
  'No se pudo quitar el insumo',
  ({ packageSupplyId }) => removePackageSupplyRequest(packageSupplyId)
);

/**
 * Asks the courier for the label. The tracking arrives a few seconds later
 * through a task, so the reload right after may still not show it.
 *
 * @param {{orderId: number, deliveryId: number}} params
 */
export const labelDelivery = writeThenReload(
  'fulfillment/labelDelivery',
  'No se pudo etiquetar',
  ({ deliveryId }) => labelDeliveryRequest(deliveryId)
);

/** The courier took the boxes. @param {{orderId: number}} params */
export const dispatchOrder = writeThenReload(
  'fulfillment/dispatchOrder',
  'No se pudo despachar',
  ({ orderId }) => dispatchOrderRequest(orderId)
);
